import { LruCache } from './lru-cache';

/**
 * Minimal, dependency-free markdown renderer for assistant messages, plus a
 * streaming-aware cache. The architecture — not the parser — is the point:
 *
 *  - Finished messages are parsed once and cached by content hash, so scrolling
 *    a long transcript never re-parses anything (the T3 Code highlight-cache
 *    pattern).
 *  - While a message is still streaming we bypass the cache and re-render on
 *    each delta, because a half-received message would only pollute the cache.
 *
 * The output is a plain HTML string and is bound through Angular's `[innerHTML]`,
 * which runs it through Angular's HTML sanitizer — defence-in-depth on top of the
 * escaping done here. A real syntax highlighter (shiki / highlight.js) would slot
 * in at {@link renderFencedCode} behind the very same cache.
 */

const MAX_CACHE_ENTRIES = 400;
const MAX_CACHE_BYTES = 8 * 1024 * 1024;
const renderedCache = new LruCache<string>(MAX_CACHE_ENTRIES, MAX_CACHE_BYTES);

export function renderMarkdownCached(text: string, streaming: boolean): string {
	if (streaming) {
		// Streaming: never read or write the cache — re-render the partial text.
		return renderMarkdown(text);
	}
	const key = hashText(text);
	const cached = renderedCache.get(key);
	if (cached !== undefined) {
		return cached;
	}
	const html = renderMarkdown(text);
	renderedCache.set(key, html, html.length * 2);
	return html;
}

export function renderMarkdown(input: string): string {
	const src = (input ?? '').replace(/\r\n?/g, '\n');

	// 1. Lift fenced code blocks out first so no inline rule touches their body.
	//    Each is parked behind an "@@CSCB:n@@" sentinel: plain ASCII that survives
	//    trim()/escaping and is vanishingly unlikely to appear in real content.
	const codeBlocks: string[] = [];
	const withoutCode = src.replace(
		/```([^\n`]*)\n([\s\S]*?)```/g,
		(_match, lang: string, code: string) => {
			codeBlocks.push(renderFencedCode(lang, code));
			return `@@CSCB:${codeBlocks.length - 1}@@`;
		}
	);

	// 2. Render block by block (blocks are separated by blank lines).
	const html = withoutCode
		.split(/\n{2,}/)
		.map(block => renderBlock(block.trim()))
		.filter(block => block.length > 0)
		.join('\n');

	// 3. Restore the fenced code blocks.
	return html.replace(/@@CSCB:(\d+)@@/g, (_match, index: string) => codeBlocks[Number(index)] ?? '');
}

function renderFencedCode(lang: string, code: string): string {
	const language = lang.trim().replace(/[^a-z0-9#+_-]/gi, '');
	const cls = language ? ` class="language-${language}"` : '';
	const body = escapeHtml(code.replace(/\n$/, ''));
	return `<pre class="cs-markdown__pre"><code${cls}>${body}</code></pre>`;
}

function renderBlock(block: string): string {
	if (block.length === 0) {
		return '';
	}
	if (/^@@CSCB:\d+@@$/.test(block)) {
		return block; // a fenced code block on its own line — restored in step 3
	}

	// Horizontal rule: a block that is nothing but --- / *** / ___ (3+).
	if (/^(-{3,}|\*{3,}|_{3,})$/.test(block)) {
		return '<hr class="cs-markdown__hr">';
	}

	const lines = block.split('\n');

	// Heading — also handles a heading glued to the next block with no blank line
	// (e.g. "### Title\n- item"): emit the heading, then render the remainder.
	const heading = lines[0].match(/^(#{1,6})\s+(.*)$/);
	if (heading) {
		const level = heading[1].length;
		const html  = `<h${level} class="cs-markdown__h">${renderInline(heading[2])}</h${level}>`;
		const rest  = lines.slice(1).join('\n').trim();
		return rest ? html + renderBlock(rest) : html;
	}

	if (lines.every(line => /^\s*[-*+]\s+/.test(line))) {
		const items = lines
			.map(line => `<li>${renderInline(line.replace(/^\s*[-*+]\s+/, ''))}</li>`)
			.join('');
		return `<ul class="cs-markdown__list">${items}</ul>`;
	}

	if (lines.every(line => /^\s*\d+[.)]\s+/.test(line))) {
		const items = lines
			.map(line => `<li>${renderInline(line.replace(/^\s*\d+[.)]\s+/, ''))}</li>`)
			.join('');
		return `<ol class="cs-markdown__list">${items}</ol>`;
	}

	if (lines.every(line => /^\s*>\s?/.test(line))) {
		const inner = lines.map(line => line.replace(/^\s*>\s?/, '')).join('\n');
		return `<blockquote class="cs-markdown__quote">${renderInline(inner)}</blockquote>`;
	}

	// GitHub-flavoured table: a header row, a delimiter row (---|:--:|--:), then body rows.
	// Like headings, a table may be glued to preceding prose with no blank line
	// ("So if passengers were the driver:\n| Flight | … |") — find it anywhere in the block.
	for (let i = 0; i + 1 < lines.length; i++) {
		if (lines[i].includes('|') && isTableSeparator(lines[i + 1])) {
			const before = lines.slice(0, i).join('\n').trim();
			const prefix = before ? renderBlock(before) : '';
			return prefix + renderTable(lines.slice(i));
		}
	}

	return `<p>${renderInline(block)}</p>`;
}

function isTableSeparator(line: string): boolean {
	return /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)+\|?\s*$/.test(line);
}

function splitTableRow(line: string): string[] {
	return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(cell => cell.trim());
}

/** A cell that reads as a number/currency/percent — used to auto-right-align money columns. */
function isNumericCell(value: string): boolean {
	const t = value.replace(/\*\*/g, '').trim();
	if (t === '' || t === '—' || t === '-') { return false; }
	// Sign may appear before or after the currency symbol ("-€ 5" and "€ -5" both count).
	return /^[-+(]?\s*[€$£]?\s*[-+]?\s*\d[\d.,\s]*\)?\s*%?\s*[MKB]?$/i.test(t);
}

function renderTable(lines: string[]): string {
	const header   = splitTableRow(lines[0]);
	const bodyRows = lines.slice(2).filter(line => line.includes('|')).map(splitTableRow);

	// Alignment: an explicit marker in the delimiter row wins; otherwise a column whose
	// body is mostly numeric is right-aligned so currency figures line up on the decimal.
	const aligns = splitTableRow(lines[1]).map((spec, i) => {
		const left = spec.startsWith(':'), right = spec.endsWith(':');
		if (left && right) { return 'center'; }
		if (right)         { return 'right'; }
		if (left)          { return 'left'; }
		let num = 0, total = 0;
		for (const row of bodyRows) {
			const c = row[i];
			if (c !== undefined && c !== '') { total++; if (isNumericCell(c)) { num++; } }
		}
		return total > 0 && num / total >= 0.6 ? 'right' : '';
	});
	const cls = (i: number) => (aligns[i] ? ` class="cs-markdown__cell--${aligns[i]}"` : '');

	const head = header.map((cell, i) => `<th${cls(i)}>${renderInline(cell)}</th>`).join('');
	const body = bodyRows
		.map(cells => {
			const tds = header.map((_, i) => `<td${cls(i)}>${renderInline(cells[i] ?? '')}</td>`).join('');
			return `<tr>${tds}</tr>`;
		})
		.join('');

	return `<div class="cs-markdown__table-wrap">`
		+ `<table class="cs-markdown__table"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`
		+ `</div>`;
}

function renderInline(text: string): string {
	// Protect inline code spans ("@@CSIC:n@@") before escaping/formatting the rest.
	const codes: string[] = [];
	let s = text.replace(/`([^`]+)`/g, (_match, code: string) => {
		codes.push(`<code class="cs-markdown__code">${escapeHtml(code)}</code>`);
		return `@@CSIC:${codes.length - 1}@@`;
	});

	s = escapeHtml(s);

	// Links: [label](url) and the CommonMark angle-bracket form [label](<url with spaces>) —
	// the latter is the only way to write a destination containing spaces, which real doc URLs
	// have (e.g. ".../Workforce Optimisation/..."). A bare unbracketed destination still may not
	// contain whitespace, per CommonMark: without that rule an unmatched "(" would swallow prose.
	// Only safe URL schemes survive; anything else degrades to the bare label.
	s = s.replace(
		/\[([^\]]+)\]\(\s*(?:&lt;(.+?)&gt;|([^)\s]+))(?:\s+&quot;.*?&quot;)?\s*\)/g,
		(_match, label: string, bracketed: string | undefined, bare: string | undefined) => {
			// escapeHtml already ran, so an angle-bracketed destination arrives as &lt;…&gt; and any
			// query separator as &amp; — hence the lazy (.+?), which must be able to span those.
			const url = bracketed ?? bare ?? '';
			// Encode spaces only. encodeURI() would re-encode the '%' of an already-escaped
			// destination ("/a%20b" -> "/a%2520b"); the entities escapeHtml left behind are already
			// valid inside an href attribute.
			return isSafeUrl(url)
				? `<a href="${url.replace(/ /g, '%20')}" target="_blank" rel="noopener noreferrer">${label}</a>`
				: label;
		}
	);

	// Emphasis.
	s = s
		.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
		.replace(/__([^_]+)__/g, '<strong>$1</strong>')
		.replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>')
		.replace(/(^|[^_])_([^_]+)_/g, '$1<em>$2</em>');

	// Soft line breaks within a paragraph.
	s = s.replace(/\n/g, '<br>');

	// Restore inline code spans.
	return s.replace(/@@CSIC:(\d+)@@/g, (_match, index: string) => codes[Number(index)] ?? '');
}

function escapeHtml(value: string): string {
	return value
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
}

function isSafeUrl(url: string): boolean {
	const trimmed = url.trim();
	if (/^(https?:|mailto:)/i.test(trimmed)) {
		return true;
	}
	// Reject any other explicit scheme (javascript:, data:, …); allow relative,
	// root-relative and anchor links.
	return !/^[a-z][a-z0-9+.-]*:/i.test(trimmed);
}

function hashText(value: string): string {
	// FNV-1a 32-bit — fast, good enough as a cache key for message bodies.
	let hash = 0x811c9dc5;
	for (let i = 0; i < value.length; i++) {
		hash ^= value.charCodeAt(i);
		hash = Math.imul(hash, 0x01000193);
	}
	return (hash >>> 0).toString(16);
}
